import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useNovelRepository } from '@/hooks/useNovelRepository';

export function useReaderManagement() {
  const { novel, selectedNovel } = useNovel();
  if (!novel.id || !selectedNovel) {
    throw new Error('useReaderManagement must be used within a NovelProvider with a loaded novel.');
  }

  const { novelId } = selectedNovel;

  const { t } = useTranslation();
  const { backend, user } = useFirebase();
  const { updateNovelItem } = useNovelRepository();

  const [newReaderEmail, setNewReaderEmail] = useState('');
  const [readerError, setReaderError] = useState<string | null>(null);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);

  const isUpdatingReader = updatingMemberId !== null;

  const handleAddReader = async () => {
    if (!newReaderEmail.trim() || !backend) return;
    const queryTerm = newReaderEmail.trim();

    if (user?.email && queryTerm.toLowerCase() === user.email.toLowerCase()) {
      setReaderError(t('dashboard.settings.cannot_add_self'));
      return;
    }
    if (user?.displayName && queryTerm.toLowerCase() === user.displayName.toLowerCase()) {
      setReaderError(t('dashboard.settings.cannot_add_self'));
      return;
    }

    setReaderError(null);
    setUpdatingMemberId('adding');

    try {
      let resolvedUid: string | null = null;

      try {
        const res = await backend.novels.resolveUserByQuery(queryTerm);
        resolvedUid = res.uid;
      } catch (err: unknown) {
        if (
          err &&
          typeof err === 'object' &&
          'code' in err &&
          err.code === 'functions/not-found'
        ) {
          if (queryTerm.includes('@')) {
            const email = queryTerm.toLowerCase();
            const currentPending = novel.pendingReaders || [];
            if (currentPending.includes(email)) {
              setReaderError(t('dashboard.settings.already_invited'));
              setUpdatingMemberId(null);
              return;
            }
            try {
              await backend.novels.sendEmailInvitation({
                email,
                novelId: novelId || '',
                ownerId: novel.ownerId,
                role: 'reader',
                novelTitle: novel.title || 'Untitled Novel',
              });
            } catch (emailErr) {
              console.error('Failed to trigger email invitation dispatch:', emailErr);
            }
            setNewReaderEmail('');
            setUpdatingMemberId(null);
            return;
          } else {
            setReaderError(t('dashboard.settings.display_name_not_found'));
            setUpdatingMemberId(null);
            return;
          }
        }
        throw err;
      }

      if (resolvedUid === novel.ownerId) {
        setReaderError(t('dashboard.settings.user_is_owner'));
        return;
      }

      const readersMap = novel.readers || {};
      if (resolvedUid in readersMap) {
        setReaderError(t('dashboard.settings.already_reader'));
        return;
      }

      // Add novel member securely via Cloud Function to keep interacted list in sync!
      await backend.novels.addNovelMember({ novelId: novelId || '', memberUid: resolvedUid, role: 'reader' });
      setNewReaderEmail('');
    } catch (err: unknown) {
      console.error('Failed to add reader:', err);
      setReaderError(t('dashboard.settings.add_error'));
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemoveReader = async (readerId: string) => {
    if (!backend) return;
    setUpdatingMemberId(readerId);
    try {
      await backend.novels.removeNovelMember({
        ownerId: novel.ownerId,
        novelId: novelId || '',
        memberUid: readerId,
        role: 'reader',
      });
    } catch (err) {
      console.error('Failed to remove reader:', err);
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemovePendingReader = async (email: string) => {
    if (!backend) return;
    setUpdatingMemberId(email);
    try {
      await backend.novels.removeNovelMember({
        ownerId: novel.ownerId,
        novelId: novelId || '',
        email,
        role: 'reader',
      });
    } catch (err) {
      console.error('Failed to remove pending reader:', err);
    } finally {
      setUpdatingMemberId(null);
    }
  };

  return {
    newReaderEmail,
    setNewReaderEmail,
    readerError,
    isUpdatingReader,
    updatingMemberId,
    handleAddReader,
    handleRemoveReader,
    handleRemovePendingReader,
  };
}
